/**
 * WP7b / #46616 — in-shell terminal dock.
 *
 * Opens and closes on the current verification page (no navigation). Operator
 * gated by resolveTerminalAccess; a non-operator event is ignored. Does not
 * own chat entry.
 */

import { useEffect, useState, type ReactNode } from "react";
import { Icon } from "./icons";
import { ActionButton } from "./ui";
import {
  CLOSE_TERMINAL_EVENT,
  OPEN_TERMINAL_EVENT,
  TOGGLE_TERMINAL_EVENT,
  dispatchCloseTerminal,
  type TerminalAccess,
} from "./terminal-stage";

export function TerminalDock({
  access,
}: {
  access: TerminalAccess;
}): ReactNode {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!access.allowed) {
      setOpen(false);
      return;
    }
    const onOpen = () => setOpen(true);
    const onClose = () => setOpen(false);
    const onToggle = () => setOpen((v) => !v);
    window.addEventListener(OPEN_TERMINAL_EVENT, onOpen);
    window.addEventListener(CLOSE_TERMINAL_EVENT, onClose);
    window.addEventListener(TOGGLE_TERMINAL_EVENT, onToggle);
    return () => {
      window.removeEventListener(OPEN_TERMINAL_EVENT, onOpen);
      window.removeEventListener(CLOSE_TERMINAL_EVENT, onClose);
      window.removeEventListener(TOGGLE_TERMINAL_EVENT, onToggle);
    };
  }, [access.allowed]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      dispatchCloseTerminal();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!access.allowed || !open) return null;

  return (
    <div
      className="vp-terminal-dock"
      data-verification-terminal-dock=""
      data-terminal-open="true"
      role="region"
      aria-label="Terminal"
    >
      <div className="vp-terminal-dock-head">
        <span className="vp-terminal-dock-title">
          <Icon name="code" size={14} />
          Terminal
        </span>
        <ActionButton actionId="util.terminal" variant="ghost" onClick={() => dispatchCloseTerminal()}>
          Close
        </ActionButton>
      </div>
      <div className="vp-terminal-dock-body">
        Local operator shell. Stays on this verification page.
      </div>
    </div>
  );
}
